import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { Info, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { reduceToasts, emptyToastState, type Toast, type ToastTone } from '@/lib/toasts';
import { reportToast } from '@/lib/toast-report';

// =====================================================================
// Toaster — the app-level floating stack, over the pure store in lib/toasts.ts
// (which is where the dismissal semantics live and are tested).
//
// NOT for everything that looks like a message. The cockpit's existing amber
// notices (off-hours, "couldn't confirm a human") are INLINE and belong where
// they render: they explain the thing next to them and scroll with it. A toast
// is for something true about the SESSION that the SDR is not currently looking
// at — its whole value is being somewhere they aren't.
//
// Deliberately not auto-dismissing. These announce conditions, not events, and
// a condition that times out just lies. Dismissal is the SDR's call.
// =====================================================================

interface ToastApi {
  // Safe to call on every render: identical content is a no-op, a dismissed id
  // stays dismissed, and a repeat id updates in place instead of stacking.
  show: (toast: Omit<Toast, 'dismissible'> & { dismissible?: boolean }) => void;
  // The condition resolved. Removes the toast AND forgets a dismissal, so a
  // later recurrence is a fresh episode.
  clear: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToasts(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToasts must be used inside <ToastProvider>');
  return api;
}

const TONE: Record<
  ToastTone,
  { icon: typeof Info; className: string; iconClass: string; actionClass: string }
> = {
  info: {
    icon: Info,
    className: 'border-border bg-card text-card-foreground',
    iconClass: 'text-muted-foreground',
    actionClass: 'border-border hover:bg-accent',
  },
  warn: {
    icon: TriangleAlert,
    className: 'border-amber-300 bg-amber-50 text-amber-900',
    iconClass: 'text-amber-600',
    actionClass: 'border-amber-400 hover:bg-amber-100',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reduceToasts, emptyToastState);

  const show = useCallback<ToastApi['show']>((toast) => {
    dispatch({ type: 'show', toast: { dismissible: true, ...toast } });
  }, []);
  const clear = useCallback<ToastApi['clear']>((id) => dispatch({ type: 'clear', id }), []);
  const api = useMemo(() => ({ show, clear }), [show, clear]);

  // When each visible toast first appeared, so an outcome can carry how long it
  // was on screen. Not state: writing it would re-render, and nothing renders it.
  const shownAt = useRef<Map<string, number>>(new Map());

  // Report `shown` on the absent -> present transition only. A replacement (the
  // same id with new content) is the SAME impression and must not count twice,
  // which is exactly what the mic toast does on every deviceChange.
  useEffect(() => {
    const live = new Set(state.toasts.map((t) => t.id));
    for (const id of live) {
      if (!shownAt.current.has(id)) {
        shownAt.current.set(id, performance.now());
        reportToast('shown', id);
      }
    }
    for (const id of [...shownAt.current.keys()]) {
      if (!live.has(id)) shownAt.current.delete(id);
    }
  }, [state.toasts]);

  // Outcome + dwell time, read before the id leaves the map above.
  const outcome = useCallback((id: string, kind: 'dismissed' | 'action') => {
    const at = shownAt.current.get(id);
    reportToast(kind, id, at === undefined ? undefined : performance.now() - at);
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Top-centre: the Twilio panel owns the top-left corner, and a bottom
          toast would sit under the disposition buttons the SDR is aiming for. */}
      <div
        className="pointer-events-none fixed top-4 left-1/2 z-50 flex w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2"
        aria-live="polite"
        aria-atomic="false"
        data-testid="toaster"
      >
        {state.toasts.map((t) => {
          const tone = TONE[t.tone];
          const Icon = tone.icon;
          return (
            <div
              key={t.id}
              role="status"
              data-testid={`toast-${t.id}`}
              className={cn(
                'pointer-events-auto flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm shadow-lg',
                'motion-safe:animate-toast-in',
                tone.className,
              )}
            >
              <Icon className={cn('mt-0.5 size-4 shrink-0', tone.iconClass)} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="font-medium">{t.title}</div>
                {t.body ? <div className="mt-0.5 opacity-90">{t.body}</div> : null}
              </div>
              {t.action ? (
                <button
                  type="button"
                  onClick={() => {
                    outcome(t.id, 'action');
                    t.action?.onClick();
                  }}
                  className={cn(
                    'shrink-0 rounded-md border px-2 py-1 text-xs font-medium',
                    tone.actionClass,
                  )}
                  data-testid={`toast-${t.id}-action`}
                >
                  {t.action.label}
                </button>
              ) : null}
              {t.dismissible ? (
                <button
                  type="button"
                  onClick={() => {
                    outcome(t.id, 'dismissed');
                    dispatch({ type: 'dismiss', id: t.id });
                  }}
                  className="shrink-0 rounded-md p-0.5 opacity-60 hover:opacity-100"
                  aria-label="Dismiss"
                  data-testid={`toast-${t.id}-dismiss`}
                >
                  <X className="size-4" />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
